import React, { useEffect, useState, useRef } from "react";
import { api, STATUS_OPTIONS, statusPill, statusLabel, fmtEUR, fmtTL } from "../lib/api";
import { Plus, Search, Upload, Trash2, X, Loader2, Wallet, FileDown, Paperclip, AlertTriangle, Calendar, MessageCircle, Link2, Copy, Settings2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  full_name: "", passport_number: "", passport_issue_date: "", passport_expiry_date: "",
  passport_authority: "", tc_id: "", birth_date: "",
  customer_phone: "", customer_email: "", customer_address: "",
  supplier_id: "", agency_id: "", country_id: "", travel_date: "",
  appointment_date: "", appointment_time: "",
  status: "randevu_bekliyor", form_status: "bos", notes: "",
  supplier_cost_eur: 0, customer_price_eur: 0, customer_price_tl: 0, customer_currency: "EUR",
};

export default function Cases() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [refs, setRefs] = useState({ suppliers: [], agencies: [], countries: [], banks: [] });
  const [colFilters, setColFilters] = useState({});
  const [showColsMenu, setShowColsMenu] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("vp_case_cols") || "null") || {
        full_name: true, country_name: true, travel_date: true, appointment_date: true,
        supplier_name: true, customer_phone: false, form_status: true,
        total: true, remaining: true, status: true,
      };
    } catch { return { full_name: true, country_name: true, travel_date: true, appointment_date: true, supplier_name: true, form_status: true, total: true, remaining: true, status: true }; }
  });

  const toggleCol = (k) => setVisibleCols((v) => {
    const next = { ...v, [k]: !v[k] };
    localStorage.setItem("vp_case_cols", JSON.stringify(next));
    return next;
  });

  const COLUMNS = [
    { key: "full_name", label: "Ad Soyad" },
    { key: "customer_phone", label: "Telefon" },
    { key: "country_name", label: "Ülke" },
    { key: "travel_date", label: "Seyahat" },
    { key: "appointment_date", label: "Randevu" },
    { key: "supplier_name", label: "Tedarikçi" },
    { key: "form_status", label: "Form" },
    { key: "total", label: "Toplam" },
    { key: "remaining", label: "Kalan" },
    { key: "status", label: "Durum" },
  ];

  const load = async () => {
    const r = await api.get("/cases");
    setItems(r.data);
  };

  useEffect(() => {
    load();
    Promise.all([
      api.get("/suppliers"),
      api.get("/agencies"),
      api.get("/countries"),
      api.get("/banks"),
    ]).then(([s, a, c, b]) => setRefs({ suppliers: s.data, agencies: a.data, countries: c.data, banks: b.data }));
  }, []);

  const filtered = items.filter((c) => {
    const ok = q === "" ||
      c.full_name?.toLowerCase().includes(q.toLowerCase()) ||
      c.passport_number?.toLowerCase().includes(q.toLowerCase()) ||
      c.country_name?.toLowerCase().includes(q.toLowerCase()) ||
      c.customer_phone?.toLowerCase().includes(q.toLowerCase()) ||
      c.customer_email?.toLowerCase().includes(q.toLowerCase());
    const sok = statusFilter === "all" || c.status === statusFilter;
    // per-column contains-match filters
    for (const [k, v] of Object.entries(colFilters)) {
      if (!v) continue;
      const cell = String(c[k] ?? "").toLowerCase();
      if (!cell.includes(v.toLowerCase())) return false;
    }
    return ok && sok;
  });

  const startNew = () => { setEditing({ ...empty }); setOpen(true); };
  const startEdit = (c) => { setEditing({ ...c }); setOpen(true); };

  const onSaved = async () => {
    setOpen(false);
    setEditing(null);
    await load();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    await api.delete(`/cases/${id}`);
    toast.success("Kayıt silindi");
    load();
  };

  const downloadAppointmentPdf = async (id, name) => {
    try {
      const r = await api.get(`/cases/${id}/appointment-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `randevu_${(name || "musteri").replace(/\s+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Randevu PDF hazırlandı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF oluşturulamadı");
    }
  };

  const shareWhatsApp = async (id) => {
    try {
      const r = await api.post(`/cases/${id}/appointment-share`);
      window.open(r.data.whatsapp_url, "_blank");
      toast.success("WhatsApp açılıyor…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paylaşım linki oluşturulamadı");
    }
  };

  const copyPortal = async (slug) => {
    const url = `${window.location.origin}/portal/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Müşteri portal linki kopyalandı");
    } catch { toast.error(url); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="cases-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">Vize İşlemleri</div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl tracking-tight">Başvurular</h1>
        </div>
        <button
          data-testid="add-visa-case-button"
          onClick={startNew}
          className="h-11 px-5 bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors flex items-center gap-2 font-medium text-sm"
        >
          <Plus size={16} /> Yeni Vize Ekle
        </button>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="cases-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, pasaport no, ülke, telefon…"
            className="w-full h-10 pl-9 pr-3 border border-zinc-200 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0033cc]/20 focus:border-[#0033cc]"
          />
        </div>
        <select
          data-testid="cases-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 border border-zinc-200 bg-white rounded-md text-sm"
        >
          <option value="all">Tüm Durumlar</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="relative">
          <button onClick={() => setShowColsMenu(!showColsMenu)} className="h-10 px-3 border border-zinc-200 rounded-md text-sm hover:bg-zinc-100 flex items-center gap-2" data-testid="cases-cols-btn">
            <Settings2 size={13} /> Sütunlar
          </button>
          {showColsMenu && (
            <div className="absolute right-0 top-11 z-20 bg-white border border-zinc-200 rounded-md shadow-lg p-2 w-52" onMouseLeave={() => setShowColsMenu(false)}>
              {COLUMNS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded cursor-pointer text-sm" data-testid={`col-toggle-${c.key}`}>
                  <input type="checkbox" checked={!!visibleCols[c.key]} onChange={() => toggleCol(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 text-left">
            <tr className="border-b border-zinc-200">
              {visibleCols.full_name && <Th>Ad Soyad</Th>}
              {visibleCols.customer_phone && <Th>Telefon</Th>}
              {visibleCols.country_name && <Th>Ülke</Th>}
              {visibleCols.travel_date && <Th>Seyahat</Th>}
              {visibleCols.appointment_date && <Th>Randevu</Th>}
              {visibleCols.supplier_name && <Th>Tedarikçi</Th>}
              {visibleCols.form_status && <Th>Form</Th>}
              {visibleCols.total && <Th>Toplam</Th>}
              {visibleCols.remaining && <Th>Kalan</Th>}
              {visibleCols.status && <Th>Durum</Th>}
              <Th></Th>
            </tr>
            <tr className="border-b border-zinc-100 bg-white">
              {COLUMNS.filter(c => visibleCols[c.key]).map(col => (
                <th key={col.key} className="px-2 py-1">
                  <input
                    value={colFilters[col.key] || ""}
                    onChange={(e) => setColFilters(f => ({ ...f, [col.key]: e.target.value }))}
                    placeholder={`${col.label} filtrele…`}
                    className="w-full h-7 px-2 border border-zinc-200 rounded text-xs bg-white focus:outline-none focus:border-zinc-950"
                    data-testid={`col-filter-${col.key}`}
                  />
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const total = c.customer_currency === "TL" ? fmtTL(c.customer_price_tl) : fmtEUR(c.customer_price_eur);
              const rem = c.remaining_currency === "TL" ? fmtTL(c.remaining) : fmtEUR(c.remaining);
              return (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => setDetailId(c.id)} data-testid={`case-row-${c.id}`}>
                  {visibleCols.full_name && (
                    <Td className="font-medium">
                      <div className="flex items-center gap-2">
                        {c.full_name}
                        {(c.passport_expiring || c.passport_expired) && (
                          <span title={c.passport_expired ? "Pasaport süresi geçmiş" : `Pasaport ${c.passport_days_left} gün sonra bitiyor`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${c.passport_expired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`case-passport-warn-${c.id}`}>
                            <AlertTriangle size={10} /> {c.passport_expired ? "SÜRESİ GEÇMİŞ" : `${c.passport_days_left}g`}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-400">{c.passport_number || "—"}</div>
                    </Td>
                  )}
                  {visibleCols.customer_phone && <Td className="text-xs">{c.customer_phone || "—"}</Td>}
                  {visibleCols.country_name && <Td>{c.country_name}</Td>}
                  {visibleCols.travel_date && <Td>{c.travel_date || "—"}</Td>}
                  {visibleCols.appointment_date && <Td>{c.appointment_date ? `${c.appointment_date}${c.appointment_time ? " " + c.appointment_time : ""}` : "—"}</Td>}
                  {visibleCols.supplier_name && <Td>{c.supplier_name || "—"}</Td>}
                  {visibleCols.form_status && (
                    <Td>
                      <span className={`status-pill ${c.form_status === "dolu" ? "status-approved" : "status-appt"}`}>{c.form_status === "dolu" ? "Dolu" : "Boş"}</span>
                    </Td>
                  )}
                  {visibleCols.total && <Td className="tabular-nums">{total}</Td>}
                  {visibleCols.remaining && <Td className={`tabular-nums ${c.remaining > 0 ? "text-[#8a5b00] font-semibold" : "text-[#00913c] font-semibold"}`}>{rem}</Td>}
                  {visibleCols.status && <Td><span className={`status-pill ${statusPill(c.status)}`}>{statusLabel(c.status)}</span></Td>}
                  <Td>
                    <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      {c.appointment_date && (
                        <>
                          <button onClick={() => downloadAppointmentPdf(c.id, c.full_name)} className="px-2 py-1 text-xs text-[#0033cc] hover:bg-blue-50 rounded flex items-center gap-1" data-testid={`appt-pdf-${c.id}`} title="Randevu PDF (indir)">
                            <Calendar size={11} /> PDF
                          </button>
                          <button onClick={() => shareWhatsApp(c.id)} className="px-2 py-1 text-xs text-[#00913c] hover:bg-green-50 rounded flex items-center gap-1" data-testid={`appt-wa-${c.id}`} title="WhatsApp ile paylaş">
                            <MessageCircle size={11} /> WhatsApp
                          </button>
                        </>
                      )}
                      <button onClick={() => startEdit(c)} className="px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 rounded" data-testid={`edit-case-${c.id}`}>Düzenle</button>
                      <button onClick={() => copyPortal(c.portal_slug)} className="px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 rounded flex items-center gap-1" data-testid={`portal-copy-${c.id}`} title="Müşteri portalı linkini kopyala">
                        <Link2 size={11} /> Portal
                      </button>
                      <button onClick={() => onDelete(c.id)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded" data-testid={`delete-case-${c.id}`}><Trash2 size={12} /></button>
                    </div>
                  </Td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="p-8 text-center text-sm text-zinc-500">Kayıt bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && editing && <CaseModal data={editing} refs={refs} onClose={() => setOpen(false)} onSaved={onSaved} />}
      {detailId && <CaseDetail id={detailId} refs={refs} onClose={() => { setDetailId(null); load(); }} />}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 label-overline font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

function CaseModal({ data, refs, onClose, onSaved }) {
  const [form, setForm] = useState(data);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result;
      setPreview(b64);
      setScanning(true);
      try {
        const r = await api.post("/ocr/passport", { image_base64: b64 });
        setForm((f) => ({
          ...f,
          full_name: r.data.full_name || f.full_name,
          passport_number: r.data.passport_number || f.passport_number,
          passport_issue_date: r.data.passport_issue_date || f.passport_issue_date,
          passport_expiry_date: r.data.passport_expiry_date || f.passport_expiry_date,
          passport_authority: r.data.passport_authority || f.passport_authority,
          tc_id: r.data.tc_id || f.tc_id,
          birth_date: r.data.birth_date || f.birth_date,
        }));
        toast.success("Pasaport verileri okundu");
      } catch (err) {
        toast.error("OCR başarısız oldu");
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.full_name || !form.country_id) {
      toast.error("Ad Soyad ve Ülke zorunlu");
      return;
    }
    try {
      if (form.id) {
        await api.put(`/cases/${form.id}`, form);
        toast.success("Güncellendi");
      } else {
        await api.post("/cases", form);
        toast.success("Eklendi");
      }
      onSaved();
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-md w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="case-modal">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 sticky top-0 bg-white">
          <div>
            <div className="label-overline">{form.id ? "Düzenle" : "Yeni Kayıt"}</div>
            <h2 className="mt-1 font-display font-black text-2xl">Vize Başvurusu</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded" data-testid="modal-close"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* PASSPORT OCR at TOP */}
          <div className="border-2 border-dashed border-zinc-300 rounded-md p-6 bg-zinc-50" data-testid="passport-ocr-zone">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <div className="label-overline">Pasaport Tarama Aracı</div>
                <h3 className="mt-1 font-display font-bold text-lg">Fotoğraf yükleyin, alanlar otomatik dolsun</h3>
                <p className="mt-1 text-xs text-zinc-500">JPG, PNG · Gemini 3 Flash ile OCR</p>
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="passport-file-input" />
                <button
                  data-testid="passport-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={scanning}
                  className="mt-4 h-10 px-4 bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  {scanning ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {scanning ? "Taranıyor…" : "Pasaport Yükle"}
                </button>
              </div>
              {preview && (
                <img src={preview} alt="preview" className="w-32 h-32 object-cover rounded border border-zinc-200" />
              )}
            </div>
          </div>

          <Section title="Kimlik & Pasaport">
            <Field label="Ad Soyad" val={form.full_name} on={(v) => set("full_name", v)} testid="field-full-name" />
            <Field label="Pasaport Numarası" val={form.passport_number} on={(v) => set("passport_number", v)} testid="field-passport-number" />
            <Field label="Pasaport Başlangıç" type="date" val={form.passport_issue_date} on={(v) => set("passport_issue_date", v)} testid="field-passport-issue" />
            <Field label="Pasaport Bitiş" type="date" val={form.passport_expiry_date} on={(v) => set("passport_expiry_date", v)} testid="field-passport-expiry" />
            <Field label="Veren Makam" val={form.passport_authority} on={(v) => set("passport_authority", v)} testid="field-passport-authority" />
            <Field label="TC Kimlik No" val={form.tc_id} on={(v) => set("tc_id", v)} testid="field-tc-id" />
            <Field label="Doğum Tarihi" type="date" val={form.birth_date} on={(v) => set("birth_date", v)} testid="field-birth-date" />
          </Section>

          <Section title="Müşteri İletişim">
            <Field label="Telefon" val={form.customer_phone} on={(v) => set("customer_phone", v)} testid="field-customer-phone" />
            <Field label="E-posta" val={form.customer_email} on={(v) => set("customer_email", v)} testid="field-customer-email" />
            <label className="block md:col-span-2">
              <div className="text-xs text-zinc-600 mb-1">Adres</div>
              <textarea value={form.customer_address || ""} onChange={(e) => set("customer_address", e.target.value)} rows={2} className="w-full border border-zinc-200 rounded-md p-3 text-sm" data-testid="field-customer-address" />
            </label>
          </Section>

          <Section title="Randevu & Vize">
            <Select label="Ülke" val={form.country_id} on={(v) => set("country_id", v)} options={refs.countries.map((c) => ({ value: c.id, label: c.name }))} testid="field-country" />
            <Field label="Seyahat Tarihi" type="date" val={form.travel_date} on={(v) => set("travel_date", v)} testid="field-travel-date" />
            <Field label="Randevu Tarihi" type="date" val={form.appointment_date} on={(v) => set("appointment_date", v)} testid="field-appointment-date" />
            <Field label="Randevu Saati" type="time" val={form.appointment_time} on={(v) => set("appointment_time", v)} testid="field-appointment-time" />
            <Select label="Tedarikçi" val={form.supplier_id} on={(v) => set("supplier_id", v)} options={[{ value: "", label: "— Seçilmedi —" }, ...refs.suppliers.map((s) => ({ value: s.id, label: s.name }))]} testid="field-supplier" />
            <Select label="Aracı Kurum" val={form.agency_id} on={(v) => set("agency_id", v)} options={[{ value: "", label: "— Seçilmedi —" }, ...refs.agencies.map((a) => ({ value: a.id, label: a.name }))]} testid="field-agency" />
            <Select label="Durum" val={form.status} on={(v) => set("status", v)} options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))} testid="field-status" />
            <Select label="Form Durumu" val={form.form_status} on={(v) => set("form_status", v)} options={[{ value: "bos", label: "Form Boş" }, { value: "dolu", label: "Form Dolu" }]} testid="field-form-status" />
          </Section>

          <Section title="Finansal">
            <Field label="Tedarikçi Maliyeti (EUR)" type="number" val={form.supplier_cost_eur} on={(v) => set("supplier_cost_eur", parseFloat(v) || 0)} testid="field-supplier-cost" />
            <Select label="Müşteri Para Birimi" val={form.customer_currency} on={(v) => set("customer_currency", v)} options={[{ value: "EUR", label: "EUR" }, { value: "TL", label: "TL" }]} testid="field-currency" />
            <Field label="Müşteri Tutar (EUR)" type="number" val={form.customer_price_eur} on={(v) => set("customer_price_eur", parseFloat(v) || 0)} testid="field-cust-eur" />
            <Field label="Müşteri Tutar (TL)" type="number" val={form.customer_price_tl} on={(v) => set("customer_price_tl", parseFloat(v) || 0)} testid="field-cust-tl" />
          </Section>

          <div>
            <div className="label-overline mb-2">Notlar</div>
            <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3} className="w-full border border-zinc-200 rounded-md p-3 text-sm" data-testid="field-notes" />
          </div>
        </div>

        <div className="p-6 border-t border-zinc-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-10 px-4 text-sm border border-zinc-200 rounded-md hover:bg-zinc-100 transition-colors" data-testid="cancel-btn">İptal</button>
          <button onClick={save} className="h-10 px-6 text-sm bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors font-medium" data-testid="save-case-btn">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-overline mb-3">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, val, on, type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-xs text-zinc-600 mb-1">{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={val || ""}
        onChange={(e) => on(e.target.value)}
        className="w-full h-10 px-3 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0033cc]/20 focus:border-[#0033cc] bg-white"
      />
    </label>
  );
}

function Select({ label, val, on, options, testid }) {
  return (
    <label className="block">
      <div className="text-xs text-zinc-600 mb-1">{label}</div>
      <select
        data-testid={testid}
        value={val || ""}
        onChange={(e) => on(e.target.value)}
        className="w-full h-10 px-3 border border-zinc-200 rounded-md text-sm bg-white"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function CaseDetail({ id, refs, onClose }) {
  const [c, setC] = useState(null);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pf, setPf] = useState({ amount_eur: 0, amount_tl: 0, currency: "EUR", bank_id: refs.banks[0]?.id || "", notes: "" });
  const docFileRef = useRef();
  const [docKind, setDocKind] = useState("passport");

  const load = async () => {
    const [cd, pd, dd] = await Promise.all([
      api.get(`/cases/${id}`),
      api.get(`/payments`, { params: { case_id: id, party: "customer" } }),
      api.get(`/cases/${id}/documents`),
    ]);
    setC(cd.data);
    setPayments(pd.data);
    setDocuments(dd.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPayment = async () => {
    try {
      await api.post("/payments", {
        case_id: id,
        party: "customer",
        amount_eur: parseFloat(pf.amount_eur) || 0,
        amount_tl: parseFloat(pf.amount_tl) || 0,
        currency: pf.currency,
        bank_id: pf.bank_id,
        notes: pf.notes,
      });
      toast.success("Ödeme eklendi");
      setShowAdd(false);
      setPf({ amount_eur: 0, amount_tl: 0, currency: "EUR", bank_id: refs.banks[0]?.id || "", notes: "" });
      load();
    } catch {
      toast.error("Ödeme eklenemedi");
    }
  };

  const delPayment = async (pid) => {
    if (!window.confirm("Ödeme silinsin mi?")) return;
    await api.delete(`/payments/${pid}`);
    load();
  };

  const downloadInvoice = async (pid) => {
    try {
      const r = await api.get(`/payments/${pid}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `makbuz_${pid.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF oluşturulamadı"); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/cases/${id}/documents`, fd, { params: { kind: docKind }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Belge yüklendi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yüklenemedi");
    } finally {
      setUploading(false);
      if (docFileRef.current) docFileRef.current.value = "";
    }
  };

  const openDoc = async (did, filename) => {
    const r = await api.get(`/documents/${did}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
  };

  const delDoc = async (did) => {
    if (!window.confirm("Belge silinsin mi?")) return;
    await api.delete(`/documents/${did}`);
    load();
  };

  if (!c) return null;

  const kindLabel = { passport: "Pasaport", visa_form: "Vize Formu", petition: "Dilekçe", other: "Diğer" };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-md w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="case-detail">
        <div className="p-6 border-b border-zinc-200 flex items-start justify-between">
          <div>
            <div className="label-overline">Detay</div>
            <h2 className="mt-1 font-display font-black text-2xl">{c.full_name}</h2>
            <div className="mt-1 text-sm text-zinc-500 flex items-center gap-2 flex-wrap">
              {c.country_name} · {c.travel_date || "Tarih yok"} · <span className={`status-pill ${statusPill(c.status)}`}>{statusLabel(c.status)}</span>
              {(c.passport_expiring || c.passport_expired) && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${c.passport_expired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                  <AlertTriangle size={11} /> {c.passport_expired ? "Pasaport süresi geçmiş" : `Pasaport ${c.passport_days_left} gün sonra bitiyor`}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-3 border-b border-zinc-200">
          <Metric label="Toplam" value={c.customer_currency === "TL" ? fmtTL(c.customer_price_tl) : fmtEUR(c.customer_price_eur)} />
          <Metric label="Ödenen" value={c.customer_currency === "TL" ? fmtTL(c.paid_tl) : fmtEUR(c.paid_eur)} tone="green" />
          <Metric label="Kalan" value={c.remaining_currency === "TL" ? fmtTL(c.remaining) : fmtEUR(c.remaining)} tone={c.remaining > 0 ? "amber" : "green"} />
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold">Müşteri Ödemeleri</h3>
            <button onClick={() => setShowAdd(!showAdd)} className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-md hover:bg-zinc-800 flex items-center gap-2" data-testid="add-payment-btn">
              <Wallet size={14} /> Ödeme Ekle
            </button>
          </div>

          {showAdd && (
            <div className="mt-4 border border-zinc-200 rounded-md p-4 bg-zinc-50">
              <div className="grid grid-cols-2 gap-3">
                <Select label="Para Birimi" val={pf.currency} on={(v) => setPf({ ...pf, currency: v })} options={[{ value: "EUR", label: "EUR" }, { value: "TL", label: "TL" }]} testid="pay-currency" />
                <Select label="Banka" val={pf.bank_id} on={(v) => setPf({ ...pf, bank_id: v })} options={[{ value: "", label: "—" }, ...refs.banks.map((b) => ({ value: b.id, label: `${b.name} (${b.currency})` }))]} testid="pay-bank" />
                <Field label="Tutar (EUR)" type="number" val={pf.amount_eur} on={(v) => setPf({ ...pf, amount_eur: v })} testid="pay-eur" />
                <Field label="Tutar (TL)" type="number" val={pf.amount_tl} on={(v) => setPf({ ...pf, amount_tl: v })} testid="pay-tl" />
              </div>
              <div className="mt-3">
                <Field label="Açıklama" val={pf.notes} on={(v) => setPf({ ...pf, notes: v })} testid="pay-notes" />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => setShowAdd(false)} className="h-9 px-3 text-sm border rounded-md">İptal</button>
                <button onClick={addPayment} className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-md font-medium" data-testid="save-payment-btn">Kaydet</button>
              </div>
            </div>
          )}

          <div className="mt-4 grid-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="border-b border-zinc-200 text-left">
                  <Th>Tarih</Th><Th>EUR</Th><Th>TL</Th><Th>Banka</Th><Th>Not</Th><Th>Makbuz</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-100">
                    <Td>{(p.date || "").slice(0, 10)}</Td>
                    <Td>{fmtEUR(p.amount_eur)}</Td>
                    <Td>{fmtTL(p.amount_tl)}</Td>
                    <Td>{p.bank_name || "—"}</Td>
                    <Td className="text-xs text-zinc-500">{p.notes}</Td>
                    <Td><button onClick={() => downloadInvoice(p.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-zinc-200 rounded hover:bg-zinc-100" data-testid={`invoice-btn-${p.id}`}><FileDown size={12} />PDF</button></Td>
                    <Td><button onClick={() => delPayment(p.id)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 size={12} /></button></Td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-sm text-zinc-500">Ödeme yok</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-200">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold flex items-center gap-2"><Paperclip size={16} />Belge Ekleri</h3>
            <div className="flex items-center gap-2">
              <select value={docKind} onChange={(e) => setDocKind(e.target.value)} className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white" data-testid="doc-kind-select">
                <option value="passport">Pasaport</option>
                <option value="visa_form">Vize Formu</option>
                <option value="petition">Dilekçe</option>
                <option value="other">Diğer</option>
              </select>
              <input ref={docFileRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp" onChange={uploadDoc} className="hidden" data-testid="doc-file-input" />
              <button onClick={() => docFileRef.current?.click()} disabled={uploading} className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-md hover:bg-zinc-800 flex items-center gap-2 disabled:opacity-50" data-testid="doc-upload-btn">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Yükleniyor…" : "Belge Yükle"}
              </button>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-1">PDF, JPG, PNG, WEBP · en fazla 15 MB</p>

          <div className="mt-4 space-y-2">
            {documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between border border-zinc-200 rounded-md p-3 hover:bg-zinc-50" data-testid={`doc-row-${d.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded bg-zinc-100 flex items-center justify-center flex-shrink-0"><Paperclip size={14} /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.original_filename}</div>
                    <div className="text-xs text-zinc-500">{kindLabel[d.kind] || d.kind} · {(d.size / 1024).toFixed(0)} KB · {(d.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openDoc(d.id, d.original_filename)} className="px-3 py-1 text-xs border border-zinc-200 rounded hover:bg-zinc-100" data-testid={`doc-open-${d.id}`}>Aç</button>
                  <button onClick={() => delDoc(d.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
            {documents.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md">Henüz belge yok</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone = "default" }) {
  const c = { default: "text-zinc-950", green: "text-[#00913c]", amber: "text-[#8a5b00]" }[tone];
  return (
    <div className="p-6 border-r border-zinc-200 last:border-r-0">
      <div className="label-overline">{label}</div>
      <div className={`mt-2 font-display font-black text-2xl ${c}`}>{value}</div>
    </div>
  );
}
